/**
 * E2E renderer test probe — exposes PTZ position read + absolute moveTo
 * to Playwright via `globalThis.__irisDeviceProbe`.
 *
 * Per `think-in-systems` (ai.knowledge/global): a primitive. Tests poll
 * `position(deviceId)` for a deviceId-keyed PTZ snapshot and call
 * `moveTo(deviceId, ptz)` for between-iteration camera resets, instead of
 * piping every assertion through brittle DOM/CDP layers.
 *
 * The restore methods drive the real saved-camera restore and expose its
 * readiness. Token observation is installed under the same test-only gate.
 *
 * Mirrors the existing main-process precedent at
 * `studio/electron/src/main/index.ts:101-107` (`__irisLifecycle`) — same
 * IRIS_E2E_TEST=1 gate, same single-statement assignment, same
 * "test-only code path" intent. The Electron e2e fixture sets
 * IRIS_E2E_TEST=1 (verified `studio/electron/e2e/fixtures/electron.fixture.ts:321,334`).
 */
import type { Device } from '@studio/models/device';
import type { PTZ } from '@studio/models/ptz';
import { locateProxy } from '#~/agent/index.js';
import { moveTo } from '#~/control/movement/commands/move-to.js';
import { pbToJsPtz } from '#~/protobuf/index.js';
import { readDeviceDiagnostics } from './device-diagnostics.js';
import {
  observeRestoreTokenRequests,
  prepareRestoreEpoch,
  restoreEpochProbe,
  restoreEpochReady
} from './restore-epoch-probe.js';

interface DeviceProbe {
  diagnostics(deviceId: Device['id']): ReturnType<typeof readDeviceDiagnostics>;
  position(deviceId: Device['id']): PTZ;
  moveTo(deviceId: Device['id'], position: PTZ): Promise<void>;
  restoreEpoch: typeof restoreEpochProbe;
  restoreEpochReady: typeof restoreEpochReady;
  prepareRestoreEpoch: typeof prepareRestoreEpoch;
  /** Persisted preset records, for asserting what SAVE actually stored. */
  presets(deviceId: Device['id']): unknown;
  /** Raw terminal receipt of the last native-preset operation. */
  nativePresetReceipt(deviceId: Device['id']): unknown;
  /**
   * Read-only identity of the agent this device's proxy submits to, and of the
   * device as the app sees it. Identity only — no configuration, no credential.
   */
  ownerIdentity(deviceId: Device['id']): {
    agentId: string;
    roomId: string;
    embedded: boolean;
    controlUri: string;
  } | null;
  /** Start a real automatic movement through the production Cine path. */
  fastFrame(
    deviceId: Device['id'],
    center: { x: number; y: number },
    scale?: number
  ): Promise<void>;
}

// Renderer-process gate: read through the preload bridge
// (window.electron.process.env), NOT bare `process.env` — the bundler's
// define replaces `process.env` object reads with `{}` at build time, making
// the old gate compile-time false so the probe silently never installed.
// Same fix as calibration-probe (ENG-4281); bridge reads survive the build.
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

if (isE2eEnabled()) {
  observeRestoreTokenRequests();
  const probe: DeviceProbe = {
    diagnostics: (deviceId) => readDeviceDiagnostics(locateProxy(deviceId), deviceId),
    restoreEpoch: restoreEpochProbe,
    restoreEpochReady,
    prepareRestoreEpoch,
    /**
     * Read the renderer-cached PTZ position for a device, normalized to
     * JS shape `{ pan, tilt, zoom }` via `pbToJsPtz`. NOT raw protobuf —
     * the spec contract is JS-normalized scalars so the test compares
     * numbers directly without protobuf field-presence quirks.
     */
    position(deviceId: Device['id']): PTZ {
      const proxy = locateProxy(deviceId);
      // Same proxy locator the auto-tracking wiring uses for safe-position
      // reads (see auto-tracking/wiring.ts:457-460).
      return pbToJsPtz(proxy?.state.status?.device[deviceId]?.movement?.position);
    },

    /**
     * Move the device to an absolute PTZ position via the existing
     * `moveTo` command (control/movement/commands/move-to.ts). Used by
     * the IR-039 test to reset the camera to baseline `pos0` between
     * iterations so position drift across 8 directions + 2 zooms does
     * not leave the camera at a physical-range limit.
     *
     * Requires the device to honor absolute-move commands; the lab
     * lease filter is responsible for selecting an absoluteMove-capable
     * camera (NOT this probe's job — see plan-v1.md §`absoluteMove`
     * reframing).
     */
    async moveTo(deviceId: Device['id'], position: PTZ): Promise<void> {
      await moveTo(deviceId, position, false, { toolUsed: 'unknown' });
    },

    /**
     * Persisted preset records, read from the same place the preset owner
     * reads them (native-owner.ts:69). The hardware spec asserts what SAVE
     * actually stored — a real absolute position and no native target — rather
     * than counting cards, which a card would satisfy without any of that.
     */
    presets(deviceId: Device['id']): unknown {
      const proxy = locateProxy(deviceId);
      return proxy?.state.control?.studio?.device?.[deviceId]?.control?.presets?.items ?? {};
    },

    /**
     * Raw terminal receipt of the most recent native-preset operation, as the
     * encoder published it. The spec correlates operationId / generation /
     * action / mode / failure per request, so a recall that never produced a
     * new completion cannot pass by the camera already sitting on target —
     * which is exactly how a repeated same-target recall would otherwise slip
     * through.
     */
    nativePresetReceipt(deviceId: Device['id']): unknown {
      const proxy = locateProxy(deviceId);
      return proxy?.state.status?.device?.[deviceId]?.nativePreset ?? null;
    },

    /**
     * Identity of the agent this device's proxy submits to, and of the device
     * as the app sees it.
     *
     * A hardware run reads the camera through an encoder and drives it through
     * the UI; without this there is nothing to show the two are the same agent
     * rather than two that both happen to exist on the machine. The room and
     * the embedded flag say WHICH agent session is bound, and the control URI
     * is the one native-owner qualification itself reads
     * (control.studio.device[id].control.uri) — the single control endpoint,
     * not the composite driver-stack URX.
     *
     * Identity only: no configuration, no credential.
     */
    ownerIdentity(deviceId: Device['id']): {
      agentId: string;
      roomId: string;
      embedded: boolean;
      controlUri: string;
    } | null {
      const proxy = locateProxy(deviceId);
      if (!proxy) return null;
      return {
        agentId: proxy.id,
        roomId: proxy.roomId,
        embedded: proxy.embedded,
        controlUri: proxy.state.control?.studio?.device?.[deviceId]?.control?.uri ?? ''
      };
    },

    /**
     * Start a real automatic movement through the production Cine path.
     * `cine: true` is what reaches OnFastFrame and hands control to the actual
     * path owner; `cine: false` takes the ordinary absolute route and is NOT
     * ownership coverage, so it would not exercise the cancellation this is
     * here to prove.
     */
    async fastFrame(
      deviceId: Device['id'],
      center: { x: number; y: number },
      scale?: number
    ): Promise<void> {
      await moveTo(deviceId, { center, scale }, true, { toolUsed: 'cine_frame' });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisDeviceProbe = probe;
}
